// Date helpers for the crew-schedule grid. Extracted verbatim from
// ManpowerPage.tsx, plus the GridColumn model that drives the multi-week view.
//
// ── Which timezone this grid lives in (#477) ────────────────────────────
//
// The ARITHMETIC below is timezone-free and always was: getWeekSunday,
// addDays, localYmd and isWeekendYmd all take a date the caller supplies and
// round-trip through local midnight, which gives the same answer everywhere.
//
// ⚠️ THE ANCHOR IS THE PART THAT HAS A TIMEZONE — the single question "what
// calendar day is it right now". That was browser-local here and UTC on the
// API, and the two disagreeing is the whole of #477. Both sides now answer it
// in Eastern, because the business calendar is Eastern and the numbers on this
// screen are compared against a server that buckets that way.
//
// For a PM in Ohio this changes nothing, which is the point: it makes an
// answer that HAPPENED to be right because of where they were sitting right
// for a reason. On a laptop set to Mountain time, or on a phone that has
// followed somebody to a job site two zones away, the grid used to roll over
// to tomorrow at a different moment than the figures printed beside it.
//
// Use easternToday()/easternTodayYmd() for "now". Never `new Date()` directly.

export const EASTERN_TZ = 'America/New_York';

// Built ONCE — constructing an Intl.DateTimeFormat is comparatively expensive
// and buildColumns asks this per render.
const EASTERN_DAY_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TZ,
  year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * Today's business date in Eastern, as YYYY-MM-DD.
 *
 * ⚠️ The zone is NAMED, never a fixed −5/−4 offset, so this stays right
 * across the DST boundary. Mirrors easternTodayYmd in the API's
 * common/eastern-date.ts; the two have to give the same answer.
 */
export function easternTodayYmd(now: Date = new Date()): string {
  const parts = EASTERN_DAY_FORMAT.formatToParts(now);
  const get = (t: string) => parts.find((x) => x.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Today's Eastern business date as a Date at LOCAL midnight.
 *
 * The adapter for everything below, which passes Dates around. A Date built
 * from known y/m/d at local midnight round-trips through getWeekSunday,
 * addDays and localYmd in any timezone — so the only place a zone is consulted
 * stays the one call above.
 */
export function easternToday(now: Date = new Date()): Date {
  const [y, m, d] = easternTodayYmd(now).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export const DAY_ABBR = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function addDays(date: Date, n: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

export function getWeekSunday(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

export function localYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Saturday or Sunday, from a YYYY-MM-DD.
 *
 * The one definition of "not a working day" on this grid — the column model and
 * the SYSTAC bid forecast both read it, so a weekend cannot mean two things.
 * Parsed component-wise so no timezone can shift the day.
 */
export function isWeekendYmd(ymd: string): boolean {
  const [y, m, d] = ymd.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  return dow === 0 || dow === 6;
}

export function entryDateStr(workDate: string): string {
  return workDate.substring(0, 10);
}

export function formatRangeLabel(start: Date, end: Date): string {
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
}

/** Whether a grid date is today — in Eastern, matching the API's `>= today`. */
export function isTodayDate(date: Date, now: Date = new Date()): boolean {
  return localYmd(date) === easternTodayYmd(now);
}

// ─── Multi-week column model ─────────────────────────────────────────────────
//
// The grid iterates columns, not raw dates. ≤4 weeks → one column per day
// (full Sun–Sat calendar); ≥6 weeks → one column per week showing the weekly
// PEAK crew/day (read-only). Each column knows its week so week-start dividers
// and the "Week of <date>" header band derive from the column itself instead
// of hard-coded day indexes.

export const RANGE_PRESETS = [2, 4, 6, 8, 13] as const;
export type RangeWeeks = (typeof RANGE_PRESETS)[number];

/** Daily columns up to this many weeks; weekly-peak columns beyond. */
export const DAILY_MAX_WEEKS = 4;

export interface GridColumn {
  kind: 'day' | 'week';
  /** YYYY-MM-DD date(s) this column covers — 1 for day, 7 for week. */
  dates: string[];
  /** Sunday YYYY-MM-DD of the week this column belongs to. */
  weekStart: string;
  /** Header labels: top = day abbr / "wk of", bottom = date. */
  labelTop: string;
  labelBottom: string;
  isWeekend: boolean;
  isToday: boolean;
  isCurrentWeek: boolean;
  /** First column of its week — draws the 2px week-start divider. */
  isWeekStart: boolean;
}

export function isDailyGranularity(weeks: number): boolean {
  return weeks <= DAILY_MAX_WEEKS;
}

export function buildColumns(startSunday: Date, weeks: number): GridColumn[] {
  const currentWeekStart = localYmd(getWeekSunday(easternToday()));
  const cols: GridColumn[] = [];

  if (isDailyGranularity(weeks)) {
    for (let i = 0; i < weeks * 7; i++) {
      const d = addDays(startSunday, i);
      const weekStart = localYmd(getWeekSunday(d));
      const dow = d.getDay();
      cols.push({
        kind: 'day',
        dates: [localYmd(d)],
        weekStart,
        labelTop: DAY_ABBR[dow],
        labelBottom: `${d.getMonth() + 1}/${d.getDate()}`,
        isWeekend: isWeekendYmd(localYmd(d)),
        isToday: isTodayDate(d),
        isCurrentWeek: weekStart === currentWeekStart,
        isWeekStart: dow === 0,
      });
    }
  } else {
    for (let w = 0; w < weeks; w++) {
      const sunday = addDays(startSunday, w * 7);
      const weekStart = localYmd(sunday);
      const dates = Array.from({ length: 7 }, (_, i) => localYmd(addDays(sunday, i)));
      cols.push({
        kind: 'week',
        dates,
        weekStart,
        labelTop: 'wk of',
        labelBottom: sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        isWeekend: false,
        isToday: false,
        isCurrentWeek: weekStart === currentWeekStart,
        isWeekStart: true,
      });
    }
  }
  return cols;
}

/** Group daily columns into their weeks for the "Week of <date>" header band. */
export function weekGroups(columns: GridColumn[]): { weekStart: string; label: string; span: number; isCurrentWeek: boolean }[] {
  const out: { weekStart: string; label: string; span: number; isCurrentWeek: boolean }[] = [];
  for (const c of columns) {
    const last = out[out.length - 1];
    if (last && last.weekStart === c.weekStart) {
      last.span++;
    } else {
      const d = new Date(c.weekStart + 'T12:00:00');
      out.push({
        weekStart: c.weekStart,
        label: `Week of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
        span: 1,
        isCurrentWeek: c.isCurrentWeek,
      });
    }
  }
  return out;
}
